import { useCallback } from 'react'
import { useReactFlow, useUpdateNodeInternals } from '@xyflow/react'

import { WorkflowEdge, WorkflowNode } from '../types'

export const useAutoLayout = () => {
  const { setNodes, getNodes, getEdges } = useReactFlow<WorkflowNode, WorkflowEdge>()
  const updateNodeInternals = useUpdateNodeInternals()

  /**
   * Simple and effective layout that handles horizontal flow with conditional branches
   * Now with collision detection to prevent overlapping nodes
   */
  const applyConditionalAwareLayout = useCallback(() => {
    const nodes = getNodes()
    const edges = getEdges()

    // Find the root node (node with no incoming edges)
    const targetNodeIds = new Set(edges.map((edge) => edge.target))
    const rootNode = nodes.find((node) => !targetNodeIds.has(node.id))

    if (!rootNode) {
      console.warn('No root node found')
      return
    }

    // Build adjacency list for the graph
    const adjacencyList = new Map<string, Array<{ targetId: string; sourceHandle?: string }>>()
    edges.forEach((edge) => {
      if (!adjacencyList.has(edge.source)) {
        adjacencyList.set(edge.source, [])
      }
      adjacencyList.get(edge.source)!.push({
        targetId: edge.target,
        sourceHandle: edge.sourceHandle || undefined,
      })
    })

    // Layout nodes with collision detection
    const positionedNodes = new Map<string, { x: number; y: number }>()
    const processedNodes = new Set<string>()

    // Track occupied Y positions at each level to prevent overlaps
    const occupiedPositions = new Map<number, Set<number>>()

    const horizontalSpacing = 300 // Space between horizontal levels
    const verticalSpacing = 200 // Space between conditional branches
    const minVerticalGap = 180 // Minimum gap between any two nodes at same level
    const baseY = 0 // Center baseline

    // Helper function to find the nearest available Y position
    const findAvailableY = (level: number, preferredY: number): number => {
      if (!occupiedPositions.has(level)) {
        occupiedPositions.set(level, new Set())
      }

      const occupied = occupiedPositions.get(level)!

      // Check if preferred position is available
      const isAvailable = (y: number) => {
        return !Array.from(occupied).some((occupiedY) => Math.abs(occupiedY - y) < minVerticalGap)
      }

      if (isAvailable(preferredY)) {
        occupied.add(preferredY)
        return preferredY
      }

      // Find nearest available position
      let offset = minVerticalGap
      while (offset < 2000) {
        // Safety limit
        // Try above
        if (isAvailable(preferredY - offset)) {
          occupied.add(preferredY - offset)
          return preferredY - offset
        }

        // Try below
        if (isAvailable(preferredY + offset)) {
          occupied.add(preferredY + offset)
          return preferredY + offset
        }

        offset += minVerticalGap
      }

      // Fallback - should never reach here
      occupied.add(preferredY)
      return preferredY
    }

    // Process nodes level by level
    const processNode = (nodeId: string, level: number, parentY: number = baseY) => {
      if (processedNodes.has(nodeId)) return

      const x = level * horizontalSpacing
      const children = adjacencyList.get(nodeId) || []

      // Separate conditional and non-conditional children
      const conditionalChildren = children.filter(
        (child) =>
          child.sourceHandle &&
          (child.sourceHandle.includes('next-action-true') ||
            child.sourceHandle.includes('next-action-false') ||
            child.sourceHandle.includes('next-action-') ||
            child.sourceHandle.includes('next-action-insideCart') ||
            child.sourceHandle.includes('next-action-outsideCart'))
      )

      const nonConditionalChildren = children.filter(
        (child) =>
          !child.sourceHandle ||
          !(
            child.sourceHandle.includes('next-action-true') ||
            child.sourceHandle.includes('next-action-false') ||
            child.sourceHandle.includes('next-action-') ||
            child.sourceHandle.includes('next-action-insideCart') ||
            child.sourceHandle.includes('next-action-outsideCart')
          )
      )

      // Find available Y position for current node
      const availableY = findAvailableY(level, parentY)

      // Position current node
      positionedNodes.set(nodeId, { x, y: availableY })
      processedNodes.add(nodeId)

      // Process non-conditional children first (continue horizontal flow)
      nonConditionalChildren.forEach((child) => {
        processNode(child.targetId, level + 1, availableY)
      })

      // Process conditional children with vertical offsets
      if (conditionalChildren.length > 0) {
        conditionalChildren.forEach((child, index) => {
          let yOffset = 0

          if (child.sourceHandle?.includes('next-action-true')) {
            yOffset = -verticalSpacing // True branch goes up
          } else if (child.sourceHandle?.includes('next-action-false')) {
            yOffset = verticalSpacing // False branch goes down
          } else if (child.sourceHandle?.includes('next-action-insideCart')) {
            yOffset = -verticalSpacing // Inside cart branch goes up
          } else if (child.sourceHandle?.includes('next-action-outsideCart')) {
            yOffset = verticalSpacing // Outside cart branch goes down
          } else {
            const caseId = child.sourceHandle?.split('-next-action-').slice(-1)[0] || ''
            const switchCases = conditionalChildren.filter((c) => c.sourceHandle?.includes('-next-action-'))
            const nodeId = child.sourceHandle?.split('-next-action-')[0]

            // Find the index of this case in the sorted list of case IDs
            const switchNode: any = (nodes || []).find((node: any) => node.data.actionValue.id === nodeId)

            const sortedCaseIds = switchNode
              ? switchNode?.data.actionValue.settings?.cases?.map((c: any) => c.id) || []
              : []
            const caseIndex = sortedCaseIds.indexOf(caseId)
            const totalCases = switchCases.length
            const middleIndex = (totalCases - 1) / 2
            yOffset = (caseIndex - middleIndex) * verticalSpacing
          }

          const childY = availableY + yOffset
          processNode(child.targetId, level + 1, childY)
        })
      }
    }

    // Start processing from root
    processNode(rootNode.id, 0)

    // Update node positions
    const updatedNodes = nodes.map((node) => {
      const position = positionedNodes.get(node.id)
      if (position) {
        return {
          ...node,
          position: {
            x: position.x,
            y: position.y,
          },
        }
      }
      return node
    })

    setNodes(updatedNodes)
    updateNodeInternals(updatedNodes.map((node) => node.id))
  }, [getNodes, getEdges, setNodes, updateNodeInternals])

  return {
    applyConditionalAwareLayout,
  }
}
